import { memo, useEffect, useRef, useState } from "react";

// Component
import TwoPanelSplitView from "./TwoPanelSplitView";
import PanelEditorBroadcast from "./PanelEditorBroadcast";
import PanelBroadcastInfo from "./PanelBroadcastInfo";
import HeaderBar from "./HeaderBar";

// Utils
import moment from "moment";
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";
import { formatDateTimeToISO } from "./MeFormUtils";
import { countImages, getTextLength, getTotalVideoOrLink } from "./MeBroadcastUtils";

// Types
import { AllowedCondition, CreatedBroadcastData } from "./MeFormType";

// Interfaces
import { BroadcastEditorActions, BroadcastEditorContext, BroadcastMode, PickedState, UseBroadcastEditor } from "./MeCastType";
import { BROADCAST_HEADER } from "./sequence/MeCast";

const INIT_BROADCAST_DATA: CreatedBroadcastData = {
  name: "",
  status: "DRAFTED",
  receiver_type: "ALL_MEMBER",
  broadcast_type: "INSTANTLY",
  schedule_broadcasted_at: undefined,
  published_diag_rule_nodes: [],
  content: "",
};

type CardBroadcastEditorProps = {
  // config
  sequenceName: "MeForm" | "MeCast";

  mode: BroadcastMode;
  header?: string;
  context: BroadcastEditorContext;
  actions: BroadcastEditorActions;
  onBack: () => void;
  onOpenBuyCredit: () => void;
} & Pick<
  PickedState, "BillingCreditWallet" | "buttonLoadCheck"
>;

const CardBroadcastEditor = (props: CardBroadcastEditorProps) => {
  const {
    content,
    broadcastData,
    allowedSend,
    showWarnings,
    setBroadcastData,
    changeContent,
    changeData,
    submitDraft,
    submitSend
  } = useBroadcastEditor({
    mode: props.mode,
    context: props.context,
    actions: props.actions
  });

  const disabledSend = Object.values(allowedSend).some(Boolean);

  return (
    <div style={{ padding: "15px 0", height: "calc(100vh - 40px)" }}>
      <HeaderBar
        title={props.header || BROADCAST_HEADER["CREATE"]}
        onBack={props.onBack}
        broadcast={props.context.broadcast}
      />

      <TwoPanelSplitView
        defaultLeftWidth={50}
        leftPanel={
          <PanelEditorBroadcast
            keyEditor={props.context.firebase?.id}
            mode={props.mode}
            content={content}
            allowedSend={allowedSend}
            showWarnings={showWarnings}
            onChangeContent={changeContent}
            onImageUpload={(
              targetImgElement,
              index,
              state,
              imageInfo,
              remainingFilesCount
            ) => props.actions.uploadImage({
              targetImgElement,
              index,
              contentAction: state,
              imageInfo,
              remainingFilesCount
            })}
            onImageUploadBefore={(
              files,
              info,
              uploadHandler
            ) => props.actions.uploadImageBefore({
              files,
              info,
              uploadHandler
            })}
            onImageUploadError={props.actions.uploadImageError}
            onResizeEditor={props.actions.resizeEditor}
            selectedPostBroadcast={props.context.broadcast}
          />
        }
        rightPanel={
          <PanelBroadcastInfo
            sequenceName={props.sequenceName}
            isFormDetail={false}
            mode={props.mode}
            content={content}
            broadcastData={broadcastData}
            setBroadcastData={setBroadcastData}
            disabledSend={disabledSend}

            // --- context
            selectedPostBroadcast={props.context.broadcast}
            updatedByUserFirebase={props.context.updatedByUserFirebase}
            patientByLeafList={props.context.patientByLeafList}
            postCalculateCredit={props.context.postCalculateCredit}
            selectedPdrNode={props.context.initPdrNode ?? null}
            pdrNodeList={props.context.pdrNodeList || []}

            // --- Infra
            BillingCreditWallet={props.BillingCreditWallet}
            buttonLoadCheck={props.buttonLoadCheck}

            // --- actions
            onChangeData={changeData}
            onGetPatientByLeaf={props.actions.getPatientByLeaf}
            onOpenBuyCredit={props.onOpenBuyCredit}
            onSaveDraft={submitDraft}
            onSend={submitSend}
          />
        }
      />
    </div>
  )
};

/*================================ USE BROADCAST EDITOR ==============================*/

export const useBroadcastEditor = ({ mode, context, actions }: UseBroadcastEditor) => {  
  const [content, setContent] = useState<string>("");
  const [broadcastData, setBroadcastData] = useState<CreatedBroadcastData>(INIT_BROADCAST_DATA);
  const [allowedSend, setAllowedSend] = useState<AllowedCondition>({
    tooManyImages: false,
    tooManyVideos: false,
    tooLongText: false
  });

  const didInitRef = useRef(false);
  const didCalculateCreditRef = useRef(false);

  // -- init from context (EDIT / VIEW)
  useEffect(() => {
    if (mode === "CREATE") return;
    if (!context.broadcast || !context.firebase) return;
    if (didInitRef.current) return;

    didInitRef.current = true;

    setContent(context.firebase.content || "");

    setBroadcastData({
      ...INIT_BROADCAST_DATA,
      // ...context.broadcast,
      name: context.broadcast.name || "",
      status: context.broadcast.status,
      receiver_type: context.broadcast.receiver_type,
      broadcast_type: context.broadcast.broadcast_type,
      schedule_broadcasted_at: context.broadcast.schedule_broadcasted_at ?? undefined,
      published_diag_rule_nodes: context.broadcast.published_diag_rule_nodes ?? [],
      content: context.firebase.content || "",
    });
  }, [context.broadcast, context.firebase]);

  useEffect(() => {
    if (mode !== "CREATE") return;
    if (didCalculateCreditRef.current) return;

    if (context.initPdrNode) {
      didCalculateCreditRef.current = true;

      setBroadcastData(prev => ({
        ...prev,
        receiver_type: "SPECIFY",
        name: context.initPdrNode?.node_name || "",
        published_diag_rule_nodes: [context.initPdrNode?.id as number],
      }));

      actions.calculateCredit({
        receiver_type: "SPECIFY",
        published_diag_rule_nodes: [context.initPdrNode?.id as number],
      });
    } else {
      didCalculateCreditRef.current = true;

      actions.calculateCredit({
        receiver_type: "ALL_MEMBER",
        published_diag_rule_nodes: [],
      });
    }
  }, [mode, context.initPdrNode]);

  const changeContent = async (html: string) => {
    const imgCount = countImages(html);
    const videoCount = getTotalVideoOrLink(html);
    const textLen = getTextLength(html);

    setAllowedSend({
      tooManyImages: imgCount > 2,
      tooManyVideos: videoCount > 1,
      tooLongText: textLen > 1000
    });

    setContent(html);

    await actions.updateContent(html);
  };

  const changeData = (e: any, data: any) => {
    const name: keyof CreatedBroadcastData = data.name;
    let newValue = data.value;
    let shouldReCalculate = false;

    if (name === "broadcast_type") {
      if (newValue === "SCHEDULE") {
        const now = moment();
        const iso = formatDateTimeToISO(
          dateToStringWithoutTimeBE(now),
          now.format("HH:mm")
        );

        setBroadcastData(prev => ({
          ...prev,
          broadcast_type: "SCHEDULE",
          schedule_broadcasted_at: iso,
        }));
      }

      if (newValue === "INSTANTLY") {
        setBroadcastData(prev => ({
          ...prev,
          broadcast_type: "INSTANTLY",
          schedule_broadcasted_at: undefined,
        }));
      }
      return;
    }

    if (name === "schedule_broadcasted_at" && data.date && data.time) {
      newValue = formatDateTimeToISO(data.date, data.time);
    }

    if (name === "published_diag_rule_nodes" && Array.isArray(newValue)) {
      newValue = newValue.map(Number);
      shouldReCalculate = true;
    }

    if (name === "receiver_type") {
      if (newValue === "ALL_MEMBER") {
        shouldReCalculate = true;
      }

      if (newValue === "SPECIFY" && broadcastData.published_diag_rule_nodes.length > 0) {
        shouldReCalculate = true;
      }
    }

    setBroadcastData(prev => {
      const next = { ...prev, [name]: newValue };

      if (shouldReCalculate) {
        actions.calculateCredit({
          receiver_type: next.receiver_type,
          published_diag_rule_nodes:
            next.receiver_type === "ALL_MEMBER"
              ? []
              : next.published_diag_rule_nodes,
        });
      }

      return next;
    });
  };

  const submitWithStatus = (status: CreatedBroadcastData["status"]) => {
    const next = { ...broadcastData, status };

    setBroadcastData(next);
    actions.onSubmit(next);
  };

  const showWarnings = Object.values(allowedSend).some(Boolean);

  return {
    content,
    broadcastData,
    allowedSend,
    showWarnings,
    setBroadcastData,
    changeContent,
    changeData,
    submitDraft: () => submitWithStatus("DRAFTED"),
    submitSend: () => submitWithStatus("PENDING_REVIEW"),
  };
};

export default memo(CardBroadcastEditor);